import { useEffect, useState } from "react";
import { Loader2, RotateCw, Sparkles, Upload } from "lucide-react";

/**
 * iter-141 — Calm recovery affordance shown when a script appears to
 * be stuck in `indexing` state for longer than the stall threshold.
 *
 * Design intent (per spec):
 *   - Never the first thing the user sees. The pill ("Indexing…")
 *     stays alone until we cross the stall window.
 *   - Three lightweight chip-style actions. No loud red banners.
 *   - Copy is human / operational. No "parse" / "re-index" / "rebuild"
 *     jargon in the user-facing strings.
 *
 * Props:
 *   - startedAt  ISO timestamp of when indexing began (server-stamped
 *                when available; the caller can fall back to local
 *                clock on first observation).
 *   - thresholdMs ms before recovery affordance appears (default 45s).
 *   - onRefresh  () => Promise<void> — re-pulls the script doc.
 *   - onRetry    () => Promise<void> — calls the /reindex endpoint.
 *   - onReplace  () => void           — opens the file picker for replace.
 *   - busy       string | null        — which action is in-flight.
 *                                       ("refresh" | "retry" | null).
 */
export default function ScriptIndexingRecovery({
    startedAt,
    thresholdMs = 45000,
    onRefresh,
    onRetry,
    onReplace,
    busy = null,
    testidPrefix = "script-indexing-recovery",
}) {
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);

    const startMs = (() => {
        if (!startedAt) return null;
        const ms = Date.parse(startedAt);
        return Number.isFinite(ms) ? ms : null;
    })();

    if (!startMs) return null;
    const elapsed = now - startMs;
    if (elapsed < thresholdMs) return null;

    return (
        <div
            data-testid={testidPrefix}
            className="mt-3 rounded-lg border border-white/10 bg-secondary/30 p-3"
        >
            <p
                data-testid={`${testidPrefix}-copy`}
                className="text-[12px] text-neutral-300 leading-relaxed"
            >
                This is taking longer than usual. If it looks stuck, you can
                refresh the status, try reading the script again, or upload the
                file again.
            </p>
            <div className="mt-2.5 flex flex-wrap gap-2">
                <button
                    type="button"
                    data-testid={`${testidPrefix}-refresh`}
                    onClick={onRefresh}
                    disabled={Boolean(busy)}
                    className="tap inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-white/10 bg-card/60 text-[12px] text-neutral-200 hover:bg-card disabled:opacity-50"
                >
                    {busy === "refresh" ? (
                        <Loader2 size={12} className="animate-spin" />
                    ) : (
                        <RotateCw size={12} />
                    )}
                    Refresh status
                </button>
                <button
                    type="button"
                    data-testid={`${testidPrefix}-retry`}
                    onClick={onRetry}
                    disabled={Boolean(busy)}
                    className="tap inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-white/10 bg-card/60 text-[12px] text-neutral-200 hover:bg-card disabled:opacity-50"
                >
                    {busy === "retry" ? (
                        <Loader2 size={12} className="animate-spin" />
                    ) : (
                        <Sparkles size={12} />
                    )}
                    Read again
                </button>
                <button
                    type="button"
                    data-testid={`${testidPrefix}-replace`}
                    onClick={onReplace}
                    disabled={Boolean(busy)}
                    className="tap inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-white/10 bg-card/60 text-[12px] text-neutral-200 hover:bg-card disabled:opacity-50"
                >
                    <Upload size={12} />
                    Upload again
                </button>
            </div>
            <p
                data-testid={`${testidPrefix}-reassurance`}
                className="mt-2 text-[10px] uppercase tracking-[0.2em] text-neutral-500"
            >
                Your scenes, cast and continuity are kept safe.
            </p>
        </div>
    );
}
